import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

export type WeeklyByDay = Record<string, { energy?: number | string; capacity?: number | string; utilization_factor?: number | string }>;

interface EnergyCapacityChartProps {
  weeklyByDay: WeeklyByDay | null | undefined;
  daysOfWeek?: string[];
  height?: number;
}

const defaultDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const EnergyCapacityChart: React.FC<EnergyCapacityChartProps> = ({ weeklyByDay, daysOfWeek = defaultDays, height = 320 }) => {
  if (!weeklyByDay) return <div>Loading...</div>;

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const energyValues = daysOfWeek.map((d) => {
    const v = Number((weeklyByDay as any)?.[d]?.energy);
    return Number.isFinite(v) ? v : 0;
  });
  const rawCapacityValues = daysOfWeek.map((d) => {
    const v = Number((weeklyByDay as any)?.[d]?.capacity);
    return Number.isFinite(v) ? v : 0;
  });
  const capacityValues = rawCapacityValues.map((c, i) => (energyValues[i] > 0 ? c : 0));
  const utilValues: Array<number | null> = daysOfWeek.map((d) => {
    const v = Number((weeklyByDay as any)?.[d]?.energy);
    return Number.isFinite(v) ? v : null;
  });

  const data = {
    labels: daysOfWeek,
    datasets: [
      /*{
        label: 'Energy Generated (kWh)',
        data: energyValues,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },*/
      {
        label: 'Total Energy Generated (kWh)',
        data: energyValues,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false as const,
    layout: { padding: { top: 16, right: 12, bottom: 8, left: 12 } },
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Energy (kWh) and Capacity (kWp)', align: 'start' as const, padding: { top: 0, bottom: 10 } },
      tooltip: {
        callbacks: {
          afterBody: (items: any[]) => {
            const i = items && items[0] ? items[0].dataIndex : 0;
            const uv = utilValues[i];
            const e = energyValues[i];
            return e > 0 && (uv ?? uv === 0) ? [`Total Capacity (kWp): ${Number(capacityValues[i]).toFixed(2)}`] : [];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { padding: 6 },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { padding: 6 },
      },
    },
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  } as const;

  const utilLabelPlugin = {
    id: 'utilLabelPlugin',
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      ctx.save();
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      meta.data.forEach((bar: any, i: number) => {
        const uv = utilValues[i];
        const e = Number(energyValues[i]);
        if (!Number.isFinite(e) || e <= 0 || uv === null || uv === undefined) return;
        const pos = typeof bar.tooltipPosition === 'function' ? bar.tooltipPosition() : bar.getCenterPoint();
        ctx.fillText(`${uv.toFixed(2)}`, pos.x, pos.y - 8);
      });
      ctx.restore();
    },
  };

  const chartKey = `${energyValues.join('|')}|${capacityValues.join('|')}|${utilValues.map((v) => (v ?? 'n')).join('|')}`;

  return (
    <div className="chart-bar" style={{ height, minWidth: 0 }}>
      <Bar key={chartKey} data={data} options={options} plugins={[utilLabelPlugin]} />
    </div>
  );
};

export default EnergyCapacityChart;
